import { t } from '@lingui/core/macro';
import { colorSystem, Text } from '@luminovo/design-system';
import { Box } from '@mui/material';
import { SearchIcon } from 'lucide-react';
import React from 'react';

export type GlobalCommandPaletteInputProps = {
    open: boolean;
    onOpen: () => void;
    /** For the navbar's background. */
    variant?: 'dark' | 'light';
};

export function GlobalCommandPaletteInput({
    open,
    onOpen,
    variant = 'dark',
}: GlobalCommandPaletteInputProps): JSX.Element {
    const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
    const shortcut = isMac ? '⌘K' : 'Ctrl+K';

    const isDark = variant === 'dark';

    return (
        <Box
            component="button"
            onClick={onOpen}
            aria-label={t`Search`}
            aria-haspopup="dialog"
            aria-expanded={open}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 14px',
                border: 'none',
                borderRadius: '8px',
                background: isDark ? 'rgba(255, 255, 255, 0.1)' : colorSystem.neutral[1],
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                minWidth: '180px',
                '&:hover': {
                    background: isDark ? 'rgba(255, 255, 255, 0.15)' : colorSystem.neutral[2],
                },
            }}
        >
            <SearchIcon size={18} color={isDark ? colorSystem.neutral[4] : colorSystem.neutral[6]} />
            <Text
                variant="body-small"
                color={isDark ? colorSystem.neutral[4] : colorSystem.neutral[6]}
                style={{ flex: 1, textAlign: 'left' }}
            >
                {t`Search...`}
            </Text>
            <Box
                component="kbd"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2px 6px',
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : colorSystem.neutral[2],
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontFamily: 'inherit',
                    fontWeight: 500,
                    color: isDark ? colorSystem.neutral[5] : colorSystem.neutral[5],
                }}
            >
                {shortcut}
            </Box>
        </Box>
    );
}
